'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import {
  extensionToMime,
  fitsInRemainingSize,
  getInvalidExtensionFiles,
  type ValidExtensions,
} from '@/lib/utils/upload'
import { useCallback } from 'react'
import type { DragTypes, DropEvent, DropOperation } from 'react-aria'

export type DropZoneValidationError = {
  type: 'format' | 'size'
  message: string
}

export function getAttachedFiles(value: unknown): File[] {
  if (!Array.isArray(value)) return []

  return value.filter((item): item is File => item instanceof File)
}

type UseDropZoneFilesParams = {
  files: File[]
  onFilesChange: (files: File[]) => void
  validExtensions: ValidExtensions[]
  allowsMultiple: boolean
  remainingSize: number
  onValidationError?: (error: DropZoneValidationError) => void
}

// Owns the drop/select/delete flow and the format + size checks. The message is built here
// because the `dropzone` namespace belongs to this component; the caller only routes it
export function useDropZoneFiles({
  files,
  onFilesChange,
  validExtensions,
  allowsMultiple,
  remainingSize,
  onValidationError,
}: UseDropZoneFilesParams) {
  const { t } = useTranslation(['dropzone'])

  const acceptedFileMimes = validExtensions.map(extensionToMime)

  const getDropOperation = useCallback(
    (types: DragTypes): DropOperation => {
      if (acceptedFileMimes.some((type) => types.has(type))) {
        return 'copy'
      }

      return 'cancel'
    },
    [acceptedFileMimes],
  )

  const handleFileChange = useCallback(
    (incomingFiles: File[]) => {
      if (incomingFiles.length === 0) return

      const invalidFiles = getInvalidExtensionFiles(incomingFiles, validExtensions)

      if (invalidFiles.length) {
        onValidationError?.({
          type: 'format',
          message: t('dropzone:errors.format', { file: invalidFiles[0]?.name }),
        })

        return
      }

      if (!fitsInRemainingSize(incomingFiles, remainingSize)) {
        onValidationError?.({
          type: 'size',
          message: t('dropzone:errors.fileSize', { maxSize: remainingSize }),
        })

        return
      }

      // When multiple isn't allowed, a new upload replaces the previous one instead of piling up
      onFilesChange(allowsMultiple ? [...files, ...incomingFiles] : incomingFiles)
    },
    [files, onFilesChange, validExtensions, remainingSize, onValidationError, allowsMultiple, t],
  )

  const onDrop = useCallback(
    async (event: DropEvent) => {
      const droppedItems = allowsMultiple
        ? event.items.filter((item) => item.kind === 'file').map((item) => item.getFile())
        : event.items[0]?.kind === 'file'
          ? [event.items[0].getFile()]
          : []

      if (droppedItems.length === 0) return

      handleFileChange(await Promise.all(droppedItems))
    },
    [handleFileChange, allowsMultiple],
  )

  const onSelect = useCallback(
    (selectedFiles: FileList | null) => {
      if (!selectedFiles) return

      handleFileChange(Array.from(selectedFiles))
    },
    [handleFileChange],
  )

  const onDelete = useCallback(
    (file: File) => {
      onFilesChange(files.filter((attachedFile) => attachedFile !== file))
    },
    [files, onFilesChange],
  )

  return {
    acceptedFileMimes,
    getDropOperation,
    onDrop,
    onSelect,
    onDelete,
  }
}
